/**
 * Utility functions for filtering users based on areas and service types
 */

import { canBeAssignedJobs } from './role-utils';

/**
 * Filter users based on areas, service types, and role restrictions
 * @param {Array} users - Array of user objects
 * @param {Array} selectedAreas - Array of selected area strings
 * @param {Array} selectedServiceTypes - Array of selected service type IDs
 * @param {Array} availableServiceTypes - Array of available service type objects
 * @param {boolean} isHousekeeperManager - Whether the current user is a housekeeper manager
 * @param {boolean} isPropertyManager - Whether the current user is a property manager
 * @param {boolean} isDepartmentHousekeeping - Whether "Housekeeping" department is selected
 * @param {boolean} isDepartmentPropertyManagement - Whether "Property Management" department is selected
 * @returns {Array} Filtered array of users
 */
export function filterUsersByAreasAndServiceTypes(
  users,
  selectedAreas,
  selectedServiceTypes,
  availableServiceTypes,
  isHousekeeperManager = false,
  isPropertyManager = false,
  isDepartmentHousekeeping = false,
  isDepartmentPropertyManagement = false
) {
  if (!users || !Array.isArray(users)) {
    return [];
  }

  return users.filter(user => {
    // Skip inactive users
    if (user.isActive === false) {
      return false;
    }

    // Only include users who can actually be assigned to jobs
    // ADMIN users can be assigned if they have area assignments (indicating they do fieldwork)
    // GUEST_APP_MANAGER excluded - they manage but don't do the work
    if (!canBeAssignedJobs(user.role, user.areas)) {
      return false;
    }

    // Role restrictions when not in department mode
    if (isHousekeeperManager && !isDepartmentHousekeeping && !isDepartmentPropertyManagement) {
      const allowedRoles = ['HOUSEKEEPER', 'HOUSEKEEPER_MANAGER'];
      // Allow ADMIN users with areas to be shown to housekeeper managers
      const isAdminWithAreas = user.role === 'ADMIN' && user.areas && user.areas.length > 0;
      if (!allowedRoles.includes(user.role) && !isAdminWithAreas) {
        return false;
      }
    }
    
    if (isPropertyManager && !isDepartmentHousekeeping && !isDepartmentPropertyManagement) {
      // Property managers can see all users by default (no restrictions when not in department mode)
      // This maintains existing behavior for property managers
    }

    // Area filtering - user must be assigned to at least one selected area
    if (selectedAreas.length > 0) {
      const userAreas = user.areas || [];
      const hasMatchingArea = userAreas.some(area => selectedAreas.includes(area));
      if (!hasMatchingArea) {
        return false;
      }
    }

    // In department housekeeping mode, we focus on department-appropriate roles
    if (isDepartmentHousekeeping) {
      // Allow HOUSEKEEPER and HOUSEKEEPER_MANAGER roles in housekeeping department mode
      // ADMIN users with areas can also be included (they can do fieldwork)
      // GUEST_APP_MANAGER excluded as they manage but don't do the work
      const housekeepingRoles = ['HOUSEKEEPER', 'HOUSEKEEPER_MANAGER'];
      const isAdminWithAreas = user.role === 'ADMIN' && user.areas && user.areas.length > 0;
      return housekeepingRoles.includes(user.role) || isAdminWithAreas;
    }

    // In department property management mode, we focus on property management roles
    if (isDepartmentPropertyManagement) {
      // Allow INSPECTOR, PROPERTY_MANAGER, and MAINTENANCE roles in property management department mode
      // ADMIN users with areas can also be included (they can do fieldwork)
      // GUEST_APP_MANAGER excluded as they manage but don't do the work
      // HOUSEKEEPER_MANAGER is excluded to avoid overlap with housekeeping department
      const propertyManagementRoles = ['INSPECTOR', 'PROPERTY_MANAGER', 'MAINTENANCE'];
      const isAdminWithAreas = user.role === 'ADMIN' && user.areas && user.areas.length > 0;
      return propertyManagementRoles.includes(user.role) || isAdminWithAreas;
    }

    // Service type filtering (when not in any department mode)
    if (selectedServiceTypes.length > 0 && availableServiceTypes.length > 0 && !isDepartmentHousekeeping && !isDepartmentPropertyManagement) {
      // Get the selected service type objects
      const selectedServiceTypeObjects = availableServiceTypes.filter(st => 
        selectedServiceTypes.includes(st.id)
      );

      // Check if user has a role that matches any of the selected service types
      const userCanPerformAnyServiceType = selectedServiceTypeObjects.some(serviceType => {
        if (!serviceType.allowedRoles || !Array.isArray(serviceType.allowedRoles)) {
          return false;
        }
        return serviceType.allowedRoles.includes(user.role);
      });

      if (!userCanPerformAnyServiceType) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get filtering information for display purposes
 * @param {Array} selectedAreas - Array of selected area strings
 * @param {Array} selectedServiceTypes - Array of selected service type IDs
 * @param {Array} availableServiceTypes - Array of available service type objects
 * @param {number} filteredCount - Number of users after filtering
 * @param {number} totalCount - Total number of active users
 * @param {boolean} isHousekeeperManager - Whether the current user is a housekeeper manager
 * @param {boolean} isPropertyManager - Whether the current user is a property manager
 * @param {boolean} isDepartmentHousekeeping - Whether "Housekeeping" department is selected
 * @param {boolean} isDepartmentPropertyManagement - Whether "Property Management" department is selected
 * @returns {Object} Filtering information object
 */
export const getFilteringInfo = (
  selectedAreas = [],
  selectedServiceTypes = [],
  availableServiceTypes = [],
  filteredCount = 0,
  totalCount = 0,
  isHousekeeperManager = false,
  isPropertyManager = false,
  isDepartmentHousekeeping = false,
  isDepartmentPropertyManagement = false
) => {
  const filters = [];
  
  if (selectedAreas.length > 0) {
    const areaNames = selectedAreas.map(area => 
      area.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ')
    );
    filters.push(`Areas: ${areaNames.join(', ')}`);
  }
  
  // Show different filtering info based on mode
  if (isDepartmentHousekeeping) {
    // HOUSEKEEPING DEPARTMENT MODE
    filters.push(`Department: Housekeeping`);
    filters.push(`Allowed Roles: Housekeeper, Housekeeper Manager, Admin (with areas)`);
  } else if (isDepartmentPropertyManagement) {
    // PROPERTY MANAGEMENT DEPARTMENT MODE
    filters.push(`Department: Property Management`);
    filters.push(`Allowed Roles: Inspector, Property Manager, Maintenance, Admin (with areas)`);
  } else if (selectedServiceTypes.length > 0 && availableServiceTypes.length > 0) {
    // SERVICE TYPE MODE
    const selectedServiceTypeObjects = availableServiceTypes.filter(st => 
      selectedServiceTypes.includes(st.id)
    );
    const serviceTypeNames = selectedServiceTypeObjects.map(st => st.title);
    filters.push(`Service Types: ${serviceTypeNames.join(', ')}`);
    
    // Also show the roles that are allowed
    const allowedRoles = new Set();
    selectedServiceTypeObjects.forEach(serviceType => {
      if (serviceType.allowedRoles && Array.isArray(serviceType.allowedRoles)) {
        serviceType.allowedRoles.forEach(role => allowedRoles.add(role));
      }
    });
    
    if (allowedRoles.size > 0) {
      const roleNames = Array.from(allowedRoles).map(role => {
        switch (role) {
          case 'HOUSEKEEPER': return 'Housekeeper';
          case 'HOUSEKEEPER_MANAGER': return 'Housekeeper Manager';
          case 'PROPERTY_MANAGER': return 'Property Manager';
          case 'INSPECTOR': return 'Inspector';
          case 'MAINTENANCE': return 'Maintenance';
          case 'ADMIN': return 'Admin';
          case 'GUEST_APP_MANAGER': return 'Guest App Manager';
          default: return role;
        }
      });
      // Note: Admin and Guest App Manager are excluded from job assignments as they manage but don't do the work
      filters.push(`Required Roles: ${roleNames.join(', ')}`);
    }
  }
  
  return {
    hasFilters: filters.length > 0,
    filterText: filters.join(' | '),
    filteredCount,
    totalCount,
    isFiltered: filteredCount < totalCount
  };
}; 